/**
 * Desktop inventory table. Pure presentation: every action is delegated to
 * the parent via handler props so this stays a dumb, easily-testable list.
 */
import { Package, Image as ImageIcon, CurrencyDollar, MegaphoneSimple, Eye, ClockClockwise, Pencil, Trash } from "@phosphor-icons/react";
import { fmt } from "@/lib/api";
import { SelectAllCheckbox, RowCheckbox } from "@/components/BulkActionBar";

export function ProductTable({
  filteredItems,
  query,
  selection,
  postCounts,
  isAdmin,
  setGallery,
  goToPublic,
  goToCreatePost,
  viewProductPosts,
  sellNow,
  viewHistory,
  onEdit,
  onDelete,
}) {
  return (
    <section className="border border-zinc-200 hidden lg:block">
        <div className="grid grid-cols-12 px-4 py-3 bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-[0.2em] font-medium text-muted2 items-center">
          <div className="col-span-4 flex items-center gap-3">
            <SelectAllCheckbox
              allSelected={selection.allSelected}
              someSelected={selection.someSelected}
              onChange={selection.toggleAll}
              testid="inventory-select-all"
            />
            <span>Producto</span>
          </div>
          <div className="col-span-2">Categoría</div>
          <div className="col-span-1 text-right">Cant.</div>
          <div className="col-span-1 text-right">Costo</div>
          <div className="col-span-1 text-right">Venta</div>
          <div className="col-span-1 text-right">Utilidad</div>
          <div className="col-span-2 text-right">Acción</div>
        </div>
        {filteredItems.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted2">
            <Package size={28} className="mx-auto mb-3 text-zinc-400" />
            {query ? `No hay productos que coincidan con "${query}".` : "Aún no tienes productos. Agrega uno o escanea un objeto."}
          </div>
        )}
        {filteredItems.map((p) => {
          const utilidad = (p.precio_venta - p.costo) * p.cantidad;
          const imgs = (p.imagenes_base64 && p.imagenes_base64.length
            ? p.imagenes_base64
            : p.imagen_base64
            ? [p.imagen_base64]
            : []);
          const thumb = imgs[0] ? `data:image/jpeg;base64,${imgs[0]}` : null;
          return (
            <div
              key={p.id}
              className={`grid grid-cols-12 px-4 py-3 border-b border-zinc-100 text-sm items-center hover:bg-zinc-50 ${selection.isSelected(p.id) ? "bg-[#EEF2FF]" : ""}`}
              data-testid={`product-row-${p.id}`}
            >
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <RowCheckbox
                  checked={selection.isSelected(p.id)}
                  onChange={() => selection.toggle(p.id)}
                  testid={`select-${p.id}`}
                />
                <button
                  onClick={() =>
                    imgs.length &&
                    setGallery({
                      product_id: p.id,
                      images: imgs,
                      originals: p.imagenes_originales_base64 || [],
                      idx: 0,
                      nombre: p.nombre,
                    })
                  }
                  className={`relative shrink-0 w-12 h-12 border border-zinc-200 overflow-hidden bg-zinc-50 flex items-center justify-center ${
                    imgs.length ? "cursor-zoom-in hover:border-klein" : ""
                  }`}
                  disabled={!imgs.length}
                  data-testid={`thumb-${p.id}`}
                >
                  {thumb ? (
                    <>
                      <img src={thumb} alt={p.nombre} className="w-full h-full object-cover" />
                      {imgs.length > 1 && (
                        <span className="absolute bottom-0 right-0 text-[9px] bg-klein text-white px-1 leading-tight font-mono">
                          {imgs.length}
                        </span>
                      )}
                    </>
                  ) : (
                    <ImageIcon size={16} className="text-zinc-400" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => goToPublic(p)}
                    className="font-medium truncate text-left hover:text-klein hover:underline w-full"
                    data-testid={`product-name-${p.id}`}
                    data-help="Abre la página pública del producto (la que ven los compradores)."
                  >
                    {p.nombre}
                  </button>
                  <div className="text-xs text-muted2 truncate">{p.descripcion || p.para_que_sirve}</div>
                </div>
              </div>
              <div className="col-span-2 text-xs">
                <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">{p.categoria}</span>
                <span className="ml-2 text-muted2">{p.condicion}</span>
                {p.auto_publish && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#1E4FCC] bg-[#E7EEFF] border border-[#BBDEFB]"
                    title="Este producto tiene publicación automática activada"
                    data-testid={`auto-publish-on-${p.id}`}
                  >
                    🚀 AUTO
                  </span>
                )}
              </div>
              <div className="col-span-1 text-right tabular-nums">{p.cantidad}</div>
              <div className="col-span-1 text-right tabular-nums">{fmt(p.costo)}</div>
              <div className="col-span-1 text-right tabular-nums">{fmt(p.precio_venta)}</div>
              <div
                className={`col-span-1 text-right tabular-nums font-medium ${
                  utilidad >= 0 ? "text-[#00C853]" : "text-[#FF2A00]"
                }`}
              >
                {fmt(utilidad)}
              </div>
              <div className="col-span-2 flex justify-end gap-1 items-center">
                <button
                  onClick={() => sellNow(p)}
                  className="p-1.5 hover:bg-zinc-100 text-[#00C853]"
                  data-testid={`sell-now-${p.id}`}
                  data-help="Cobra al cliente en mostrador. Abre un ticket digital donde puedes agregar más productos antes de imprimir o enviar por WhatsApp."
                  title="Vender ahora"
                >
                  <CurrencyDollar size={14} weight="bold" />
                </button>
                <button
                  onClick={() => goToCreatePost(p)}
                  className="p-1.5 hover:bg-zinc-100 text-klein"
                  data-testid={`create-post-${p.id}`}
                  data-help="Genera una publicación con IA para este producto y abre el editor con todo precargado."
                  title="Crear publicación"
                >
                  <MegaphoneSimple size={14} />
                </button>
                {postCounts[p.id] > 0 && (
                  <button
                    onClick={() => viewProductPosts(p)}
                    className="px-1.5 hover:bg-klein hover:text-white text-klein border border-klein/40 text-[10px] font-mono font-bold tabular-nums leading-none flex items-center"
                    data-testid={`view-posts-${p.id}`}
                    data-help="Ver las publicaciones existentes de este producto."
                    title={`${postCounts[p.id]} publicación(es) — click para verlas`}
                  >
                    {postCounts[p.id]}
                  </button>
                )}
                <button
                  onClick={() => goToPublic(p)}
                  className="p-1.5 hover:bg-zinc-100 text-klein"
                  data-testid={`view-public-${p.id}`}
                  data-help="Abre la página pública del producto (link que comparten los compradores)."
                  title="Ver página pública"
                >
                  <Eye size={14} />
                </button>
                {isAdmin && (
                  <button
                    onClick={() => viewHistory(p)}
                    className="p-1.5 hover:bg-zinc-100 text-zinc-600"
                    data-testid={`history-${p.id}`}
                    data-help="Ver el historial completo de cambios de este producto en la auditoría."
                    title="Ver historial"
                  >
                    <ClockClockwise size={14} />
                  </button>
                )}
                <button
                  onClick={() => onEdit(p)}
                  className="p-1.5 hover:bg-zinc-100"
                  data-testid={`edit-${p.id}`}
                  data-help="Modifica nombre, costo, precio, stock o descripción de este producto."
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => onDelete(p)}
                  className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
                  data-testid={`delete-${p.id}`}
                  data-help="Elimina el producto del inventario (pide confirmación)."
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </section>
  );
}
